import { Component, HostListener, ViewChild } from '@angular/core';
import { CouponService } from '../../services/coupon.service';
import { combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Coupon } from '../../models/coupon.model';
import { FormControl, FormGroup } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Filter } from 'src/app/@shared';
import { MatSidenav } from '@angular/material/sidenav';
import { StatusCount } from '../../models/status-count.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CouponsListCardsComponent } from '../../components/coupons-list-cards/coupons.list.cards.component';
import { CouponSortDialogComponent } from '../../components/coupon-sort-dialog/coupon.sort.dialog.component';
import * as dayjs from 'dayjs';
import { Status } from '../../models/offer-promo-status';

// constants
///TODO: move this to a constants pattern
const DEFAULT_DATE_RANGE = {
  startDate: '',
  endDate: '',
};
@Component({
  selector: 'app-coupon-management',
  templateUrl: 'coupon.management.component.html',
  styleUrls: ['coupon.management.component.scss'],
})
export class CouponManagementComponent<T extends Coupon> {
  viewModel$ = combineLatest([
    this.couponService.isLoading$,
    this.couponService.couponList$,
    this.couponService.filters$,
    this.couponService.viewMode$,
    this.couponService.statusFilter$
  ]).pipe(
    map(([isLoading, coupons, filters, viewMode, statusFilter]) => {
      const filterNames = filters.map(x => x.value);
      this.filtersForm.controls.Status.patchValue(filterNames);
      return { isLoading, coupons, filters, viewMode, statusFilter };
    })
  );
  @ViewChild('couponListCards', { static: false }) couponListCards: any;
  @ViewChild('couponListTable', { static: false }) couponListTable: any;
  /** Filter drawer; use template ref — generic @ViewChild(MatSidenav) is unreliable here. */
  @ViewChild('filterSidenav') filterDrawer!: MatSidenav;
  public statusCount: StatusCount = {};

  filtersForm = new FormGroup({
    search: new FormControl<string | null>(null),
    start: new FormControl(),
    Expiry: new FormControl(),
    Status: new FormControl<string[]>([])
  });
  selection = new SelectionModel<Coupon>(true, []);
  noneValue = null;
  constructor(
    private couponService: CouponService<Coupon>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar
  ) {
  }

  ngOnInit() {
    const filters = this.filtersForm.value;
    this.couponService.reload();
    this.couponService.statusUpdated$.subscribe(res => {
      this.getStatusCount();
    })
    this.route.queryParams.subscribe((queryParams) => {
      let searchKey = queryParams['search'];

      if (searchKey) {
        this.couponService.search(searchKey);
        this.filtersForm.controls.search.setValue(searchKey);
      } else {
        this.couponService.search('');
        this.filtersForm.controls.search.setValue('');
      }
    });
  }

  getStatusCount() {
    this.couponService.getStatusCount().subscribe(res => {
      this.statusCount = res;
    })
  }

  onSearch(event: any) {
    this.couponService.search(event.target.value);
    this.clearSelection();
  }

  clearSearch() {
    this.couponService.search('');
    this.filtersForm.controls.search.setValue('');
    this.clearSelection();
  }

  clearFilters() {
    this.filtersForm.reset();
    this.couponService.search('');
    this.couponService.clearFilters();
    this.clearSelection();
  }

  removeFilter(filter: Filter) {
    if (filter.fieldName === 'start') {
      this.filtersForm.controls.start.patchValue(null);
    }
    if (filter.fieldName === 'end') {
      this.filtersForm.controls.Expiry.patchValue(null);
    }
    this.couponService.removeFilter(filter);
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
    if (this.couponListCards) {
      this.couponListCards.clearSelection();
    } else if (this.couponListTable) {
      this.couponListTable.clearSelection();
    }
  }

  resetFilters() {
    this.clearFilters();
    this.filterDrawer?.toggle();
    this.clearSelection();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('.pr1-filter-mat-sidenav')) return;
    if (target.closest('.coupon-filter-sidenav-trigger')) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.filterDrawer?.close();
  }

  onDateRangePickerOpen() {
    this.filtersForm.controls.start.reset();
    this.filtersForm.controls.Expiry.reset();
  }

  onDateRangeChange() {
    let startDate = dayjs(this.filtersForm.controls.start.value).format();
    let endDate = dayjs(this.filtersForm.controls.Expiry.value).format();
    let isbefore = dayjs(startDate).isBefore(endDate);
    let isSame = dayjs(startDate).isSame(endDate);

    if (startDate === 'Invalid Date' && endDate === 'Invalid Date') {
      this.couponService.dateRange('', '');
      return;
    }

    if (startDate === 'Invalid Date') {
      this.couponService.dateRange('', endDate);
    } else if (endDate === 'Invalid Date') {
      this.couponService.dateRange(startDate, '');
    } else if (this.filtersForm.valid) {
      this.couponService.dateRange(startDate, endDate);
    }
  }

  onFilterChange(event: any) {
    switch (event) {
      case 'start':
        if (this.filtersForm.controls.start.value === null) {
          this.couponService.removeFilterByFieldName('start');
          break;
        }
        let startDate = dayjs(this.filtersForm.controls.start.value).format();
        this.couponService.removeFilterByFieldName('start');
        const startDateFilter: Filter = {
          displayText: `Redemption Start Date ${dayjs(this.filtersForm.controls.start.value).format('MM/DD/YYYY')}`,
          fieldName: 'start',
          value: startDate
        }
        this.couponService.addFilters([startDateFilter]);
        break;
      case 'end':
        if (this.filtersForm.controls.Expiry.value === null) {
          this.couponService.removeFilterByFieldName('end');
          break;
        }
        let endDate = dayjs(this.filtersForm.controls.Expiry.value).format();
        this.couponService.removeFilterByFieldName('end');
        const endDateFilter: Filter = {
          displayText: `Expiry Date ${dayjs(this.filtersForm.controls.Expiry.value).format('MM/DD/YYYY')}`,
          fieldName: 'end',
          value: endDate
        }
        this.couponService.addFilters([endDateFilter]);
        break;
    }
  }

  deleteSelectedRecords() {
    let selectedRecordsDetails: any;
    if (this.couponListCards) {
      selectedRecordsDetails = this.couponListCards.getSelectedSectionRecords();
    } else if (this.couponListTable) {
      selectedRecordsDetails = this.couponListTable.getSelectedSectionRecords();
    }
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove selected Coupon(s)?`,
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (confirmResult) {
          const couponIds = selectedRecordsDetails.map((record: Coupon) => record.Detail.Id);
          this.couponService.deleteBulkCoupons(couponIds).subscribe({
            next: () => {
              this.matSnackBar.open(
                `Coupon(s) deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.couponService.reload();
              this.couponListCards.selection.clear();
              this.couponListTable.selection.clear();
            },
            error: (error) => {
              this.matSnackBar.open(
                `Failed to delete coupons`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.couponListCards.selection.clear();
              this.couponListTable.selection.clear();
            }
          })
        }
      });
    }
  }

  onSort() {
    this.dialog.open(CouponSortDialogComponent)
  }

  isSelected(viewModel: any, status: string) {
    return (
      viewModel.filters &&
      viewModel.filters.length > 0 &&
      viewModel.filters.find((x: any) => x.displayText === status)
    );
  }

  updateBulkStatus(status: string) {
    let selectedRecordsDetails: any;
    if (this.couponListCards) {
      selectedRecordsDetails = JSON.parse(JSON.stringify(this.couponListCards.getSelectedSectionRecords()));
    } else if (this.couponListTable) {
      selectedRecordsDetails = JSON.parse(JSON.stringify(this.couponListTable.getSelectedSectionRecords()));
    }
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      let coupons: any = selectedRecordsDetails.map((coupon: any) => coupon.Detail.Id);

      this.couponService.updateStatus(status, coupons).subscribe({
        next: () => {
          this.couponService.reload();
          if (this.couponListCards) {
            this.couponListCards.clearSelection();
          } else if (this.couponListTable) {
            this.couponListTable.clearSelection();
          }
          this.couponService.updateRecordStatus(new Date());
        }
      })
    }
  }

  toggleView(mode: string) {
    this.couponService.toggleViewMode(mode);
  }

  toggleStatusFilter(status: string[]) {    
    this.clearSelection();
    this.filtersForm.controls.Status.setValue(status);
    this.couponService.removeFilterByFieldName('status');
    const filters: Filter[] = [];
    for (let i = 0; i <= status.length - 1; i++) {
      let filter: Filter = {
        displayText: status[i],
        fieldName: 'status',
        value: status[i]
      };
      filters.push(filter);
    }

    this
      .couponService.addFilters(filters);
  }

  ngOnDestroy() {
    this.couponService.search('');
    this.couponService.clearFilters();
  }

}
